// "Choose Courier & Ship" — lets a merchant (or staff) pick, from the couriers an ADMIN assigned to
// that merchant, which one should carry the selected Not-Shipped / Booking-Failed orders, then books
// them in one go.
//
// The option list comes from the server (`/orders/courier-options`) rather than being filtered in the
// browser, so the picker can only ever offer couriers the server would also accept.
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Loader2, Truck, AlertTriangle } from 'lucide-react';
import { api } from '@/api/client';
import { Modal } from '@/components/ui/Modal';
import { useBulkProgress, BulkProgressOverlay, SHIP_CHUNK_SIZE, CHUNK_TIMEOUT_MS } from './bulk-progress';

interface Props {
  ids: string[];
  onClose: () => void;
  /** Called with the ship summary so the list can show its usual result modal + refresh. */
  onShipped: (r: { ok: number; failed: number; errors: any[] }) => void;
}

export function ChooseCourierDialog({ ids, onClose, onShipped }: Props) {
  const [courierId, setCourierId] = useState<number | ''>('');

  const { data, isLoading, error } = useQuery({
    queryKey: ['courier-options', ids],
    queryFn: async () => (await api.post('/orders/courier-options', { ids })).data as {
      merchantId: number; orderType: string; orderCount: number;
      notEditable: { id: string; orderNo: string; statusId: number }[];
      couriers: { id: number; name: string; brand?: string; code?: string; serviceType?: string; weightCategory?: string; priority?: number }[];
    },
  });

  // Assign + book runs one paced courier call per order, so it is chunked with live progress for the
  // same reason the Ship button is — a single request for 500 orders outlives the client timeout and
  // comes back looking like a failure while the booking is still going. See ./bulk-progress.
  const bulkProgress = useBulkProgress();
  const runShip = async () => {
    const errors: any[] = [];
    await bulkProgress.run<string, any>({
      label: 'Booking',
      items: ids,
      chunkSize: SHIP_CHUNK_SIZE,
      call: (chunk) =>
        api.post('/orders/bulk/assign-courier', { ids: chunk, courierId, ship: true }, { timeout: CHUNK_TIMEOUT_MS })
          .then((r) => r.data),
      tally: (r) => ({ ok: r?.shipped ?? 0, failed: r?.failed ?? 0 }),
      onChunk: (r) => { if (Array.isArray(r?.errors)) errors.push(...r.errors); },
      onChunkError: (err, chunk) => {
        const msg = err?.response?.data?.message ?? err?.message ?? 'Request failed';
        // The FIRST chunk failing usually means the whole request was rejected up front (wrong courier
        // for this merchant, already-shipped rows) — surface that message rather than burying it in a
        // per-order list the operator has to scroll.
        if (errors.length === 0) toast.error(msg);
        for (const id of chunk) errors.push({ id, source: 'system', error: msg });
      },
    });
    const failed = errors.length;
    onShipped({ ok: ids.length - failed, failed, errors });
    onClose();
  };

  const couriers = data?.couriers ?? [];
  const blocked = data?.notEditable ?? [];
  const serverMsg = (error as any)?.response?.data?.message;

  return (
    <Modal isOpen onClose={onClose} title="Choose Courier & Ship" subtitle="Only couriers assigned to this account by an admin are listed" maxWidth="md">
      <div className="space-y-4">
        {isLoading ? (
          <div className="flex items-center gap-2 text-sm text-slate-500 py-6 justify-center">
            <Loader2 size={16} className="animate-spin" /> Loading your couriers…
          </div>
        ) : serverMsg ? (
          <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg p-3">{serverMsg}</div>
        ) : (
          <>
            <div className="text-sm text-slate-600">
              Shipping <b>{data?.orderCount}</b> {data?.orderType} order{(data?.orderCount ?? 0) === 1 ? '' : 's'}.
              Pick the courier to book them with.
            </div>

            {blocked.length > 0 && (
              <div className="bg-amber-50 border border-amber-200 text-amber-800 text-xs rounded-lg p-3 flex gap-2">
                <AlertTriangle size={14} className="shrink-0 mt-0.5" />
                <span>
                  {blocked.length} selected order(s) are already shipped or cancelled and can&apos;t be re-assigned
                  ({blocked.slice(0, 4).map((o) => o.orderNo).join(', ')}{blocked.length > 4 ? '…' : ''}).
                  Deselect them first.
                </span>
              </div>
            )}

            {couriers.length === 0 ? (
              <div className="bg-slate-50 border border-slate-200 text-slate-600 text-sm rounded-lg p-4 text-center">
                No courier is assigned to this account for <b>{data?.orderType}</b> orders yet.
                <div className="text-xs text-slate-500 mt-1">Ask an admin to assign one under Merchant → Couriers.</div>
              </div>
            ) : (
              <div className="border border-slate-200 rounded-lg divide-y max-h-72 overflow-y-auto">
                {couriers.map((c) => (
                  <label key={c.id} className={`flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-slate-50 ${courierId === c.id ? 'bg-primary/5' : ''}`}>
                    <input type="radio" name="courier" checked={courierId === c.id} onChange={() => setCourierId(c.id)} />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-slate-800 truncate">{c.name}</div>
                      <div className="text-xs text-slate-500 truncate">
                        {[c.brand, c.serviceType, c.weightCategory].filter(Boolean).join(' · ') || c.code}
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            )}
          </>
        )}

        <div className="flex justify-end gap-2 pt-1">
          <button onClick={onClose} className="px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-100 rounded-lg">Cancel</button>
          <button
            onClick={() => void runShip()}
            disabled={bulkProgress.running || !courierId || blocked.length > 0 || couriers.length === 0}
            title={blocked.length > 0 ? 'Deselect the already-shipped orders first' : !courierId ? 'Pick a courier' : undefined}
            className="px-4 py-1.5 text-sm bg-primary text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 flex items-center gap-2">
            {bulkProgress.running ? <Loader2 size={14} className="animate-spin" /> : <Truck size={14} />}
            {bulkProgress.running ? 'Booking…' : `Assign & Ship (${ids.length})`}
          </button>
        </div>
      </div>
      {bulkProgress.progress && <BulkProgressOverlay progress={bulkProgress.progress} />}
    </Modal>
  );
}
